import { Component } from '@verndale/core';
import { LoadSequenceBasic } from '../../helpers/animations';

class Header extends Component {
  stickyClass = 'header--sticky';
  navOpen = false;
  desktopNavOpen = false;
  searchOpen = false;
  qs = window.matchMedia('(min-width: 1280px)');
  observer: MutationObserver;

  constructor(el: HTMLElement) {
    super(el);
    const parentElement = this.el.parentElement;
    this.observer = new MutationObserver(this.observeBodyChildren.bind(this));
    if (!parentElement) return;
    this.observer.observe(parentElement, { attributes: false, childList: true, subtree: false });
  }

  setupDefaults() {
    this.dom = {
      $openNavBtn: this.el.querySelector<HTMLElement>('.header__menu-open'),
      $closeNavBtn: this.el.querySelector<HTMLElement>('.header__menu-close'),
      $nav: this.el.querySelector<HTMLElement>('.header__nav--mobile'),
      $navButtons: this.el.querySelectorAll<HTMLElement>(
        '.header__nav--mobile .header__nav-btn:not(.header__nav-btn--back)'
      ),
      $navToggle: this.el.querySelector<HTMLElement>('.header__menu-nav-toggle'),
      $navButtonsDesktop: this.el.querySelectorAll<HTMLElement>(
        '.header__nav--desktop .header__nav-list .header__nav-btn'
      ),
      $submenusDesktop: this.el.querySelectorAll<HTMLElement>('.header__nav-tab'),
      $submenusDesktopSm: this.el.querySelectorAll<HTMLElement>('.header__nav-tab--small'),
      $navDesktop: this.el.querySelector<HTMLElement>('.header__nav--desktop'),
      $navDesktopDropdown: this.el.querySelector<HTMLElement>('.header__nav-dropdown'),
      $submenuButtons: this.el.querySelectorAll<HTMLElement>(
        '.header__nav-submenu-item [role="menuitem"]'
      ),
      $desktopSearchBtn: this.el.querySelector<HTMLElement>('.header__menu-search-btn'),
      $contactUsBtn: this.el.querySelector<HTMLElement>('.button--contact-us'),
      $langBtn: this.el.querySelector<HTMLElement>('.language-btn__btn'),
      $innerMenusDesktop: this.el.querySelectorAll<HTMLElement>('.inner-menu'),
      $utilityNav: this.el.querySelector<HTMLElement>('.utility-nav-mobile')
    };

    // Position submenus
    (this.dom.$submenusDesktopSm as NodeList)?.forEach(submenu => {
      const menuButton = document.querySelector(
        `[aria-controls="${(submenu as HTMLElement).getAttribute('id')}"]`
      );
      const menuButtonLeft = menuButton?.getBoundingClientRect().left || 0;
      const submenuOffset = (window.innerWidth - 1152) / 2;

      (submenu as HTMLElement).style.marginLeft = `${Math.round(
        menuButtonLeft - submenuOffset - 34
      )}px`;
    });

    this.qs = window.matchMedia('(min-width: 1280px)');
    if (this.qs.matches) {
      LoadSequenceBasic([{ element: this.el, addScale: false }]);
    } else {
      // to avoid animation jumps, opacity is originally set to 0 in css
      // then, if the page is loaded in a narrow breakpoint, the animation won't start
      // in that case, we set the opacity to 1 here so when the breakpoint is changed the header is visible
      (this.el as HTMLElement).style.opacity = '1';
    }

    this.updateBannerHeightVar();
  }

  addListeners() {
    window.addEventListener('scroll', this.handleScroll.bind(this));
    window.addEventListener('LANG_MENU_OPEN', this.handleLangMenuOpen.bind(this));
    window.addEventListener('DESKTOP_SEARCH_CLOSE', this.handleDesktopSearchClose.bind(this));

    document.addEventListener('keydown', this.handleDocumentKeyDown.bind(this));
    document.addEventListener('click', this.handleDocumentClick.bind(this));

    (this.dom.$openNavBtn as HTMLElement)?.addEventListener('click', this.toggleNav.bind(this));
    (this.dom.$closeNavBtn as HTMLElement)?.addEventListener('click', this.toggleNav.bind(this));
    (this.dom.$desktopSearchBtn as HTMLElement)?.addEventListener(
      'click',
      this.toggleSearch.bind(this)
    );
  }

  observeBodyChildren(mutationsList: MutationRecord[]) {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        this.updateBannerHeightVar();
        return;
      }
    }
  }

  updateBannerHeightVar() {
    const banner = document.querySelector('.alert-banner');

    if (!banner) {
      document.documentElement.style.setProperty('--banner-height', '0px');
      this.observer?.disconnect();
      return;
    }

    const bannerHeight = Math.ceil((banner as HTMLElement).offsetHeight + 20);
    const windowHeight = window.innerHeight;

    document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
    document.documentElement.style.setProperty('--vh', `${windowHeight}px`);
  }

  handleDocumentClick(e: Event) {
    let targetType, currentTarget;
    const eventTarget = e.target as HTMLElement;
    const elementSelectors = {
      navButton: '.header__nav--mobile .header__nav-btn:not(.header__nav-btn--back)',
      backButton: '.header__nav--mobile .header__nav-btn--back',
      navButtonDesktop: '.header__nav--desktop .header__nav-list .header__nav-btn',
      submenuButtonDesktop:
        '.header__nav--desktop .header__nav-submenu-item button[role="menuitem"]',
      desktopSearchBtn: 'button[aria-controls="search-desktop"]',
      langBtnDesktop: 'button[aria-controls="language-menu-desktop"]'
    };

    if ((currentTarget = eventTarget.closest(elementSelectors.navButton))) {
      targetType = 'navButton';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.backButton))) {
      targetType = 'backButton';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.navButtonDesktop))) {
      targetType = 'navButtonDesktop';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.submenuButtonDesktop))) {
      targetType = 'submenuButtonDesktop';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.desktopSearchBtn))) {
      targetType = 'desktopSearchBtn';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.langBtnDesktop))) {
      targetType = 'langBtnDesktop';
    } else if ((currentTarget = eventTarget.closest('.header__nav--desktop'))) {
      targetType = 'headerNavDesktopChild';
    } else if ((currentTarget = eventTarget.closest('.header'))) {
      targetType = 'headerChild';
    }

    const clickOutsideNav =
      eventTarget.classList.contains('header__menu') ||
      eventTarget.classList.contains('header__nav--list') ||
      eventTarget.classList.contains('header__container');

    if (!targetType || clickOutsideNav) {
      if (this.desktopNavOpen) this.resetNavDesktop();
      if (this.searchOpen) this.toggleSearch();
      if (document.querySelector('.language-btn__btn--open')) {
        window.dispatchEvent(new CustomEvent('DESKTOP_NAV_OPEN', { bubbles: true }));
      }

      return;
    }

    currentTarget = currentTarget as HTMLElement;

    switch (targetType) {
      case 'navButton':
        this.handleNavButtonTap(currentTarget);
        break;
      case 'backButton':
        this.handleBackButtonTap(currentTarget);
        break;
      case 'navButtonDesktop':
        console.log(currentTarget, 'handle document click');
        this.handleNavButtonClick(currentTarget);
        break;
      case 'submenuButtonDesktop':
        this.handleSubmenuButtonClick(e, currentTarget);
        break;
    }
  }

  handleDocumentKeyDown(e: Event) {
    let targetType, currentTarget;
    const ke = e as KeyboardEvent;
    const eventTarget = document.activeElement as HTMLElement;
    const elementSelectors = {
      navButtonDesktop: '.header__nav--desktop .header__nav-list .header__nav-btn',
      submenuButtonDesktop: '.header__nav--desktop .header__nav-submenu-item [role="menuitem"]',
      contactBtn: '.button--contact-us',
      langBtn: '.language-btn__btn',
      desktopSearchBtn: '.header__menu-search-btn',
      featuredProduct: '.featured-product',
      thirdLevelMenuItems: '.header__nav-tab-right a, .header__nav-tab-right button'
    };

    if (ke.key === 'Escape' || ke.key === 'Esc') {
      if (this.searchOpen) {
        this.toggleSearch();
        (this.dom.$desktopSearchBtn as HTMLElement).focus();
      } else if (this.desktopNavOpen) {
        const activeNavButton = document.querySelector('.header__nav-btn--active') as HTMLElement;
        this.resetNavDesktop();
        activeNavButton?.focus();
      } else {
        const langButton = document.querySelector('.language-btn__btn--open') as HTMLElement;

        if (langButton) {
          // force close lang on esc
          window.dispatchEvent(new CustomEvent('DESKTOP_NAV_OPEN', { bubbles: true }));
          langButton.focus();
        } else {
          const activeElement = document.activeElement as HTMLElement;
          activeElement.blur();
        }
      }
    }

    if (!eventTarget) return;

    if ((currentTarget = eventTarget.closest(elementSelectors.navButtonDesktop))) {
      targetType = 'navButtonDesktop';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.submenuButtonDesktop))) {
      targetType = 'submenuButtonDesktop';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.contactBtn))) {
      targetType = 'contactBtn';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.langBtn))) {
      targetType = 'langBtn';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.desktopSearchBtn))) {
      targetType = 'desktopSearchBtn';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.featuredProduct))) {
      targetType = 'featuredProduct';
    } else if ((currentTarget = eventTarget.closest(elementSelectors.thirdLevelMenuItems))) {
      targetType = 'thirdLevelMenuItems';
    }

    if (!targetType) return;

    currentTarget = currentTarget as HTMLElement;

    switch (targetType) {
      case 'navButtonDesktop':
        this.handleNavButtonKeyDown(e, currentTarget);
        break;
      case 'submenuButtonDesktop':
        this.handleSubmenuArrowNav(e, currentTarget);
        break;
      case 'featuredProduct':
        this.handleFeaturedProductKeyDown(e, currentTarget);
        break;
      case 'thirdLevelMenuItems':
        this.handlethirdLevelArrowNav(e, currentTarget);
        break;
      case 'contactBtn':
        this.handleContactBtnKeyDown(e);
        break;
    }
  }

  handleScroll() {
    if (window.scrollY > 50) {
      this.el.classList.add(this.stickyClass);
    } else {
      this.el.classList.remove(this.stickyClass);
    }
  }

  handleLangMenuOpen() {
    this.resetNavDesktop();
    this.searchOpen && this.toggleSearch();
  }

  // Navigation - Mobile
  toggleNav() {
    this.navOpen = !this.navOpen;

    const firstNav = this.el.querySelector('.header__nav--mobile .header__nav-list') as HTMLElement;

    (this.dom.$nav as HTMLElement)?.classList.toggle('hidden', !this.navOpen);
    (this.dom.$nav as HTMLElement)?.setAttribute('aria-hidden', String(!this.navOpen));
    (this.dom.$utilityNav as HTMLElement)?.classList.toggle('hidden', !this.navOpen);
    (this.dom.$utilityNav as HTMLElement)?.setAttribute('aria-expanded', String(this.navOpen));
    firstNav?.setAttribute('aria-expanded', String(this.navOpen));
    firstNav?.classList.toggle('hidden', !this.navOpen);

    (this.dom.$navToggle as HTMLElement)?.classList.toggle(
      'header__menu-nav-toggle--open',
      this.navOpen
    );

    document.body.classList.toggle('no-scroll', this.navOpen);

    !this.navOpen && this.resetNav();
  }

  resetNav() {
    const submenus = this.el.querySelectorAll('.header__nav-list .header__nav-list');
    const firstLevelNavItems = this.el.querySelectorAll(
      ':scope .header__nav-list > .header__nav-item'
    );

    const firstLevelUtilityItems = this.el.querySelectorAll(
      ':scope .utility-nav-mobile > .header__nav-item'
    );

    submenus.forEach(subMenu => {
      subMenu.classList.add('hidden');
      subMenu.setAttribute('aria-expanded', 'false');
    });

    const utilitySubMenus = this.el.querySelectorAll('.utility-nav-mobile .header__nav-list');
    const utilitySubMenusItems = this.el.querySelectorAll('.utility-nav-mobile .utility-nav__btn');

    utilitySubMenus.forEach(subMenu => {
      subMenu.classList.add('hidden');
      subMenu.setAttribute('aria-expanded', 'false');
    });

    utilitySubMenusItems.forEach(subMenu => {
      subMenu.classList.remove('hidden');
    });

    firstLevelNavItems.forEach(navItem => navItem.classList.remove('hidden'));
    firstLevelUtilityItems.forEach(navItem => navItem.classList.remove('hidden'));
  }

  handleNavButtonTap(currentTarget: HTMLElement) {
    // Navigate up to the parent li element
    const currentNavItem = currentTarget.closest('.header__nav-item') as HTMLElement;

    // Hide all direct sibling li elements of the current nav item
    // Use the child combinator '>' to select only direct children
    const siblingNavItems = currentNavItem.parentElement?.querySelectorAll(
      ':scope > .header__nav-item'
    );
    siblingNavItems?.forEach(navItem => {
      if (navItem !== currentNavItem) {
        navItem.classList.add('hidden');
      }
    });

    // Find and show the ul with class 'header__nav-list' that is a sibling of the current nav button
    const siblingNavList = currentNavItem.querySelector('.header__nav-list');
    if (siblingNavList) {
      siblingNavList.classList.remove('hidden');
      siblingNavList.setAttribute('aria-expanded', 'true');
    }

    const currentNav = currentNavItem.parentElement?.parentElement?.querySelector(
      '.header__nav-list'
    ) as HTMLElement;

    const utilityNav = currentNav.parentElement?.querySelector(
      '.utility-nav-mobile'
    ) as HTMLElement;

    const currentItem = currentTarget.closest('.header__nav-item');
    const isUtility = ((currentItem as HTMLElement)?.dataset as DOMStringMap).utilityItem as string;

    if (currentNav && isUtility) {
      currentNav.classList.add('hidden');
      currentNav.setAttribute('aria-expanded', 'false');
      currentTarget?.classList.add('hidden');
    } else {
      utilityNav?.classList.add('hidden');
    }
  }

  handleBackButtonTap(currentTarget: HTMLElement) {
    // Navigate up to the parent ul of the back button (the currently visible submenu)
    const currentSubmenu = currentTarget.closest('.header__nav-list') as HTMLElement;

    // Hide the current submenu
    if (currentSubmenu) {
      currentSubmenu.classList.add('hidden');
      currentSubmenu.setAttribute('aria-expanded', 'false');
    }

    // Find the parent li of the current submenu
    const parentNavItem = currentSubmenu.closest('.header__nav-item') as HTMLElement;

    // Show all direct sibling li elements of the parent nav item
    // Use the child combinator '>' to select only direct children
    const siblingNavItems = parentNavItem.parentElement?.querySelectorAll(
      ':scope > .header__nav-item'
    );
    siblingNavItems?.forEach(navItem => {
      navItem.classList.remove('hidden');
    });

    const currentNav = parentNavItem.parentElement?.parentElement?.querySelector(
      '.header__nav-list'
    ) as HTMLElement;

    if (currentNav) {
      currentNav.classList.remove('hidden');
      currentNav.setAttribute('aria-expanded', 'true');
    }
    const currentButton = currentTarget.parentElement?.parentElement?.parentElement?.querySelector(
      '.utility-nav__btn'
    ) as HTMLElement;

    if (currentButton) {
      currentButton.classList.remove('hidden');
    }

    const utilityNav = currentNav.parentElement?.querySelector(
      '.utility-nav-mobile'
    ) as HTMLElement;

    if (utilityNav) {
      utilityNav.classList.remove('hidden');
    }
  }

  // Navigation - Desktop
  toggleSearch() {
    const desktopSearchBtn = this.dom.$desktopSearchBtn as HTMLElement;
    let customEvent: string;

    this.searchOpen = !this.searchOpen;

    desktopSearchBtn.classList.toggle('header__menu-search-btn--open', this.searchOpen);
    desktopSearchBtn.blur();

    this.searchOpen && this.resetNavDesktop();

    this.searchOpen
      ? (customEvent = 'DESKTOP_SEARCH_OPEN')
      : (customEvent = 'DESKTOP_SEARCH_CLOSE');

    window.dispatchEvent(new CustomEvent(customEvent, { bubbles: true }));
  }

  handleDesktopSearchClose() {
    const desktopSearchBtn = this.dom.$desktopSearchBtn as HTMLElement;
    this.searchOpen = false;
    desktopSearchBtn.classList.toggle('header__menu-search-btn--open', this.searchOpen);
  }

  resetNavDesktop() {
    this.desktopNavOpen = false;

    (this.dom.$navDesktopDropdown as HTMLElement)?.classList.add('hidden');
    (this.dom.$navDesktopDropdown as HTMLElement)?.setAttribute('aria-hidden', 'true');

    (this.dom.$innerMenusDesktop as NodeList).forEach(innerMenu => {
      (innerMenu as HTMLElement).classList.add('hidden');
      (innerMenu as HTMLElement).setAttribute('aria-hidden', 'true');
    });

    (this.dom.$submenusDesktop as NodeList).forEach(submenu => {
      (submenu as HTMLElement).classList.add('hidden');
      (submenu as HTMLElement).setAttribute('aria-hidden', 'true');
      (submenu as HTMLElement).setAttribute('aria-expanded', 'false');
    });

    (this.dom.$navButtonsDesktop as NodeList).forEach(navButton => {
      (navButton as HTMLElement).classList.remove('header__nav-btn--active');
    });

    this.resetSubnavDesktop();
  }

  resetSubnavDesktop() {
    const featuredProducts = this.el.querySelectorAll('.featured-product');

    featuredProducts.forEach(featuredProduct => {
      (featuredProduct as HTMLElement).classList.remove('hidden');
      (featuredProduct as HTMLElement).setAttribute('aria-hidden', 'false');
    });

    (this.dom.$submenuButtons as NodeList).forEach(submenuBtn => {
      (submenuBtn as HTMLElement).classList.remove('button--slide-bg-selected');

      const submenuId = (submenuBtn as HTMLElement).getAttribute('aria-controls');
      const targetSubmenu = document.querySelector(`#${submenuId}`) as HTMLElement;
      const targetSubmenuList = targetSubmenu?.querySelector('.inner-menu__list') as HTMLElement;

      targetSubmenu?.classList.toggle('hidden', true);
      targetSubmenu?.setAttribute('aria-hidden', 'true');
      targetSubmenuList?.setAttribute('aria-expanded', 'false');
    });
  }

  handleNavButtonClick(currentTarget: HTMLElement) {
    const submenuId = currentTarget.getAttribute('aria-controls');
    const targetSubmenu = this.el.querySelector(`#${submenuId}`) as HTMLElement;
    const hasFeaturedProduct = targetSubmenu?.querySelector('.featured-product') as HTMLElement;
    const isTargetOpen = !targetSubmenu?.classList.contains('hidden');
    const isNavOpen = !(this.dom.$navDesktopDropdown as HTMLElement)?.classList.contains('hidden');

    this.searchOpen && this.toggleSearch();

    this.desktopNavOpen = !isNavOpen || !isTargetOpen;
    this.desktopNavOpen && this.animateLevel1Nav(targetSubmenu);

    if (this.desktopNavOpen) {
      // notify desktop nav is open
      const event = new CustomEvent('DESKTOP_NAV_OPEN', { bubbles: true });
      window.dispatchEvent(event);
    }

    (this.dom.$navDesktopDropdown as HTMLElement)?.classList.toggle('hidden', !this.desktopNavOpen);
    (this.dom.$navDesktopDropdown as HTMLElement)?.setAttribute(
      'aria-hidden',
      String(!this.desktopNavOpen)
    );

    (this.dom.$submenusDesktop as NodeList).forEach(submenu => {
      const selected = this.desktopNavOpen
        ? (submenu as HTMLElement).getAttribute('id') === submenuId
        : false;
      (submenu as HTMLElement).classList.toggle('hidden', !selected);
      (submenu as HTMLElement).setAttribute('aria-hidden', String(!selected));
      (submenu as HTMLElement).setAttribute('aria-expanded', String(selected));
    });

    (this.dom.$navButtonsDesktop as NodeList).forEach(navButton => {
      (navButton as HTMLElement).classList.toggle(
        'header__nav-btn--active',
        navButton === currentTarget
      );
    });

    if (!this.desktopNavOpen) {
      currentTarget.classList.remove('header__nav-btn--active');
      currentTarget.blur();
    } else if (!hasFeaturedProduct) {
      const firstSubmenuButton = targetSubmenu.querySelector(
        'a[role="menuitem"]'
      ) as HTMLElement;
      this.resetSubnavDesktop();
      this.handleSubmenuButtonClick(new CustomEvent('AUTO_OPEN'), firstSubmenuButton);
    }
  }

  handleSubmenuButtonClick(e: Event, currentTarget: HTMLElement) {
    const submenuId = currentTarget?.getAttribute('aria-controls');
    if (!submenuId) return;
    const targetSubmenu = document.querySelector(`#${submenuId}`) as HTMLElement;
    const targetSubmenuList = targetSubmenu.querySelector('.inner-menu__list') as HTMLElement;
    const isTargetOpen = !targetSubmenu?.classList.contains('hidden');
    const featuredProduct = targetSubmenu?.parentElement?.querySelector(
      '.featured-product'
    ) as HTMLElement;
    const activeClass = 'button--slide-bg-selected';

    this.resetSubnavDesktop();

    currentTarget.classList.toggle(activeClass, !isTargetOpen);
    featuredProduct?.classList.toggle('hidden', !isTargetOpen);
    featuredProduct?.setAttribute('aria-hidden', String(!isTargetOpen));
    targetSubmenu?.classList.toggle('hidden', isTargetOpen);
    targetSubmenu?.setAttribute('aria-hidden', String(isTargetOpen));
    targetSubmenuList?.setAttribute('aria-expanded', String(!isTargetOpen));

    isTargetOpen && currentTarget.blur();

    if (!isTargetOpen) {
      this.animateLevel2Nav(targetSubmenu);

      if (e.type === 'AUTO_OPEN') return;

      // if keyboard triggered, focus first item
      const pe = e as PointerEvent;
      if (!pe.clientX || (pe.clientX === 0 && pe.clientY === 0)) {
        const firstFocusableElement = targetSubmenu.querySelector(
          '[role="menuitem"]'
        ) as HTMLElement;
        firstFocusableElement?.focus();
      }
    }
  }

  getTopMenuInfo() {
    const activeNavButton = this.el.querySelector('.header__nav-btn--active') as HTMLElement;
    const navButtons = Array.from(this.dom.$navButtonsDesktop as NodeList) as HTMLElement[];
    const activeNavButtonIndex = navButtons.indexOf(activeNavButton);
    const nextFocusableAfterActive =
      navButtons[activeNavButtonIndex + 1] || (this.dom.$contactUsBtn as HTMLElement);
    const focusedElement = document.activeElement as HTMLElement;
    const focusedElementIndex = navButtons.indexOf(focusedElement);
    const nextFocusable =
      navButtons[focusedElementIndex + 1] || (this.dom.$contactUsBtn as HTMLElement);
    const lastFocusable = navButtons[navButtons.length - 1];

    return {
      activeNavButton,
      navButtons,
      activeNavButtonIndex,
      nextFocusableAfterActive,
      nextFocusable,
      lastFocusable
    };
  }

  // Keyboard navigation
  handleNavButtonKeyDown(e: Event, currentTarget: HTMLElement) {
    const ke = e as KeyboardEvent;
    const submenuId = currentTarget.getAttribute('aria-controls');
    const submenu = document.querySelector(`#${submenuId}`) as HTMLElement;
    const menuItems = submenu
      ? (Array.from(submenu?.querySelectorAll('[role="menuitem"]')) as HTMLElement[])
      : null;
    const firstMenuItem = menuItems ? menuItems[0] : null;

    if (ke.key === 'Tab' && !ke.shiftKey) {
      const { nextFocusable } = this.getTopMenuInfo();

      ke.preventDefault();
      nextFocusable.focus();
      return;
    }

    if ((submenuId && ke.key === 'Enter') || ke.key === ' ') {
      ke.preventDefault();
      const { activeNavButton } = this.getTopMenuInfo();
      console.log(currentTarget, 'currentTarget nav button key down');
      this.handleNavButtonClick(currentTarget);

      if (this.desktopNavOpen) {
        firstMenuItem?.focus();
      } else {
        activeNavButton?.focus();
      }
    }
  }

  handleSubmenuArrowNav(e: Event, currentTarget: HTMLElement) {
    const ke = e as KeyboardEvent;

    if (ke.key === 'Tab') {
      ke.preventDefault();

      const { nextFocusableAfterActive, activeNavButton } = this.getTopMenuInfo();

      if (!ke.shiftKey) {
        nextFocusableAfterActive.focus();
      } else {
        activeNavButton?.focus();
      }

      return;
    }

    const submenu = currentTarget.closest('.header__nav-submenu') as HTMLElement;
    const submenuButtons = Array.from(
      submenu.querySelectorAll('[role="menuitem"]')
    ) as HTMLElement[];
    const currentSubmenuButtonIndex = submenuButtons.indexOf(currentTarget);
    const nextSubmenuButton = submenuButtons[currentSubmenuButtonIndex + 1];
    const prevSubmenuButton = submenuButtons[currentSubmenuButtonIndex - 1];
    const navTab = currentTarget.closest('.header__nav-tab') as HTMLElement;
    const navTabId = navTab?.getAttribute('id');
    const navButton = document.querySelector(`[aria-controls="${navTabId}"]`) as HTMLElement;
    const rightTabElement = navTab.querySelector(
      '.header__nav-tab-right .featured-product'
    ) as HTMLElement;

    if (ke.key === 'ArrowDown' && nextSubmenuButton) {
      ke.preventDefault();
      nextSubmenuButton.focus();
    } else if (ke.key === 'ArrowUp') {
      ke.preventDefault();

      if (prevSubmenuButton) {
        prevSubmenuButton.focus();
      } else {
        navButton.focus();
      }
    }

    if (ke.key === 'ArrowRight' && rightTabElement) {
      ke.preventDefault();
      rightTabElement.focus();
    }
  }

  handleFeaturedProductKeyDown(e: Event, currentTarget: HTMLElement) {
    const ke = e as KeyboardEvent;

    if (ke.key === 'Tab') {
      ke.preventDefault();

      const { nextFocusableAfterActive, activeNavButton } = this.getTopMenuInfo();

      if (!ke.shiftKey) {
        nextFocusableAfterActive.focus();
      } else {
        activeNavButton?.focus();
      }

      return;
    }

    if (ke.key === 'ArrowLeft') {
      const submenu = currentTarget.closest('.header__nav-tab') as HTMLElement;
      const firstMenuItem = submenu.querySelector('[role="menuitem"]') as HTMLElement;

      ke.preventDefault();
      firstMenuItem.focus();
    }
  }

  handlethirdLevelArrowNav(e: Event, currentTarget: HTMLElement) {
    // use left and right arrows to focus next/previous menu item
    const ke = e as KeyboardEvent;
    const currentElement = currentTarget as HTMLElement;
    const key = ke.key;

    if (key === 'Tab') {
      ke.preventDefault();

      const { nextFocusableAfterActive, activeNavButton } = this.getTopMenuInfo();

      if (!ke.shiftKey) {
        nextFocusableAfterActive.focus();
      } else {
        activeNavButton?.focus();
      }

      return;
    }

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      ke.preventDefault();
      const menuWrapper = currentElement.closest('.inner-menu') as HTMLElement;
      const menuItems = menuWrapper.querySelectorAll('a, button') as NodeListOf<HTMLElement>;
      const activeItem = document.activeElement as HTMLElement;
      const activeIndex = Array.from(menuItems).indexOf(activeItem);
      const nextIndex = key === 'ArrowLeft' ? activeIndex - 1 : activeIndex + 1;

      if (nextIndex >= 0 && nextIndex < menuItems.length) {
        const nextItem = menuItems[nextIndex];
        nextItem.focus();
      } else if (key === 'ArrowLeft' && activeIndex === 0) {
        const activeMenuItem = currentElement
          .closest('.header__nav-tab')
          ?.querySelector('.header__nav-tab-left .button--slide-bg-selected') as HTMLElement;
        activeMenuItem.focus();
      } else if (key === 'ArrowRight' && activeIndex === menuItems.length - 1) {
        const firstItem = menuItems[0];
        firstItem.focus();
      }
    }
  }

  handleContactBtnKeyDown(e: Event) {
    const ke = e as KeyboardEvent;

    if (ke.key === 'Tab' && ke.shiftKey) {
      const { lastFocusable } = this.getTopMenuInfo();

      ke.preventDefault();
      lastFocusable.focus();
    }
  }

  // Animation sequences
  animateLevel1Nav(submenu: HTMLElement) {
    const rightTab = submenu.querySelector('.header__nav-tab-right') as HTMLElement;
    const leftTab = submenu.querySelector('.header__nav-tab-left') as HTMLElement;
    const menuItems = Array.from(leftTab.querySelectorAll('[role="menuitem"]')) as HTMLElement[];
    const elements = [
      { element: submenu, addScale: false },
      { element: rightTab, addScale: false, offset: 0.2 }
    ];

    menuItems.forEach((item, index) => {
      elements.push({ element: item, addScale: false, offset: 0.3 + index * 0.05 });
    });

    LoadSequenceBasic(elements);
  }

  animateLevel2Nav(submenu: HTMLElement) {
    const innerMenuList = submenu.querySelector('.inner-menu__list') as HTMLElement;
    const menuItems = Array.from(
      innerMenuList.querySelectorAll('.inner-menu__item')
    ) as HTMLElement[];
    const elements: { element: HTMLElement; addScale: boolean; offset?: string | number }[] = [];
    const lastLink = submenu.querySelector('.link--internal:last-child') as HTMLElement;

    menuItems.forEach((item, index) => {
      elements.push({ element: item, addScale: false, offset: index * 0.05 });
    });

    lastLink &&
      elements.push({ element: lastLink, addScale: false, offset: menuItems.length * 0.05 });

    LoadSequenceBasic(elements);
  }
}

export default Header;
